import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAccount } from '~/contexts/UserAccount/UserAccountContext';
import { useNotifications } from '~/contexts/NotificationContext';
import { usePostalAddressesRepository } from './rest';
import { CreateAddressDto, PostalAddress } from './types';

const queryKey = 'postal-addresses';

export const usePostalAddresses = () => {
    const { id: accountId } = useAccount();
    const { getPostalAddresses } = usePostalAddressesRepository();
    return useQuery(queryKey, () => getPostalAddresses({ accountId }));
};

export const useAddressCreate = () => {
    const client = useQueryClient();
    const { pushSuccessNotification } = useNotifications();
    const { id: accountId } = useAccount();
    const { createPostalAddress } = usePostalAddressesRepository();

    const mutationFn = useCallback(
        (addressDto: CreateAddressDto) => createPostalAddress({ accountId, addressDto }),
        [createPostalAddress, accountId],
    );

    return useMutation(mutationFn, {
        onSuccess: async () => {
            pushSuccessNotification('New address has been added.');
            await client.invalidateQueries(queryKey);
        },
    });
};

export const useAddressUpdate = () => {
    const client = useQueryClient();
    const { pushSuccessNotification } = useNotifications();
    const { updatePostalAddress } = usePostalAddressesRepository();

    return useMutation(updatePostalAddress, {
        onSuccess: async () => {
            pushSuccessNotification('The address has been updated.');
            await client.invalidateQueries(queryKey);
        },
    });
};

export const useAddressDelete = () => {
    const client = useQueryClient();
    const { pushSuccessNotification, pushApiErrorNotification } = useNotifications();
    const { deletePostalAddress } = usePostalAddressesRepository();

    return useMutation(deletePostalAddress, {
        onSuccess: async () => {
            pushSuccessNotification('This address has been removed.');
            await client.invalidateQueries(queryKey);
        },
        onError: () => pushApiErrorNotification('An error occurred while removing address.'),
    });
};

export const useAddressDefaultToggle = () => {
    const { updatePostalAddress } = usePostalAddressesRepository();
    const { pushSuccessNotification } = useNotifications();
    const client = useQueryClient();

    const toggleDefault = useCallback(
        (address: PostalAddress) =>
            updatePostalAddress({ id: address.id, addressDto: { isDefault: !address.isDefault } }),
        [updatePostalAddress],
    );

    return useMutation(toggleDefault, {
        onMutate: (props) => {
            const data = client.getQueryData<Array<PostalAddress>>(queryKey);
            if (data) {
                const newData = data.map((address) => ({ ...address, isDefault: address.id === props.id }));
                newData.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
                client.setQueryData(queryKey, newData);
            }
            return data;
        },
        onError: (_err, _props, context) => {
            client.setQueryData(queryKey, context);
        },
        onSuccess: async () => {
            pushSuccessNotification('Default address has been updated.');
            await client.invalidateQueries(queryKey);
        },
    });
};
